/* Dataset sub-screen — replaces the project detail view while `datasetMode`
 * is set, exactly like the Climate / Assets / Nodes / Lines config screens.
 *
 * Owns the dataset list, the polling loop while an import runs, and the series
 * catalogue; the three tabs are presentation over that state.
 */
import React, { useCallback, useEffect, useRef, useState } from 'react';
import { CenecLoadingBlock } from './CenecSpinner';
import { API, spanLabel, statusColor } from './datasetCommon';
import DatasetOverviewTab from './DatasetOverviewTab';
import DatasetSummaryTab from './DatasetSummaryTab';
import DatasetExplorerTab from './DatasetExplorerTab';

const TABS = [
  { key: 'overview', label: 'Overview' },
  { key: 'summary',  label: 'Summary'  },
  { key: 'explorer', label: 'Explorer' },
];

const ACTIVE = ['staged', 'reconciling', 'loading', 'compressing', 'aggregating'];

const DatasetView = ({ project, onBack }) => {
  const [tab, setTab] = useState('overview');
  const [datasets, setDatasets] = useState([]);
  const [activeId, setActiveId] = useState(null);
  const [dataset, setDataset] = useState(null);      // detail incl. report
  const [series, setSeries] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [deleting, setDeleting] = useState(false);
  const pollRef = useRef(null);

  // ---- list ---------------------------------------------------------------
  const loadList = useCallback(async () => {
    try {
      const res = await fetch(`${API}/api/projects/${project.id}/datasets`);
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      const rows = await res.json();
      const list = Array.isArray(rows) ? rows : [];
      setDatasets(list);
      setError(null);
      setActiveId((cur) => {
        if (cur && list.some((d) => d.id === cur)) return cur;
        if (!list.length) return null;
        return [...list].sort((a, b) => String(b.created_at || '').localeCompare(String(a.created_at || '')))[0].id;
      });
      return list;
    } catch (err) {
      setError(err.message || String(err));
      return [];
    } finally {
      setLoading(false);
    }
  }, [project.id]);

  useEffect(() => { loadList(); }, [loadList]);

  // ---- detail + polling ----------------------------------------------------
  const loadDetail = useCallback(async (id) => {
    if (!id) { setDataset(null); return null; }
    try {
      const res = await fetch(`${API}/api/result-datasets/${id}`);
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      const body = await res.json();
      setDataset(body);
      return body;
    } catch (err) {
      setError(err.message || String(err));
      return null;
    }
  }, []);

  useEffect(() => {
    if (!activeId) { setDataset(null); setSeries([]); return undefined; }
    let cancelled = false;

    const tick = async () => {
      const body = await loadDetail(activeId);
      if (cancelled || !body) return;
      if (!ACTIVE.includes(body.status)) {
        clearInterval(pollRef.current);
        pollRef.current = null;
        loadList();
      }
    };

    tick();
    // 2 s while the import walks 350,640 rows; the interval clears itself as
    // soon as the dataset settles into ready or failed.
    pollRef.current = setInterval(tick, 2000);
    return () => {
      cancelled = true;
      clearInterval(pollRef.current);
      pollRef.current = null;
    };
  }, [activeId, loadDetail, loadList]);

  // ---- series catalogue ----------------------------------------------------
  const status = dataset && dataset.status;
  useEffect(() => {
    if (!activeId || status !== 'ready') { setSeries([]); return; }
    let cancelled = false;
    (async () => {
      try {
        const res = await fetch(`${API}/api/result-datasets/${activeId}/series`);
        if (!res.ok) throw new Error(`HTTP ${res.status}`);
        const rows = await res.json();
        if (!cancelled) setSeries(Array.isArray(rows) ? rows : []);
      } catch (err) {
        if (!cancelled) setError(err.message || String(err));
      }
    })();
    return () => { cancelled = true; };
  }, [activeId, status]);

  // ---- mutations -----------------------------------------------------------
  const onStarted = useCallback(async (created) => {
    setTab('overview');
    if (created && created.id) setActiveId(created.id);
    await loadList();
  }, [loadList]);

  const onResume = useCallback(async () => {
    if (!activeId) return;
    try {
      const res = await fetch(`${API}/api/result-datasets/${activeId}/resume`, { method: 'POST' });
      if (!res.ok) {
        const body = await res.json().catch(() => ({}));
        throw new Error(body.error || `HTTP ${res.status}`);
      }
      await loadDetail(activeId);
      if (!pollRef.current) pollRef.current = setInterval(() => loadDetail(activeId), 2000);
    } catch (err) {
      setError(`Resume failed: ${err.message || String(err)}`);
    }
  }, [activeId, loadDetail]);

  const onDelete = useCallback(async () => {
    if (!activeId) return;
    setDeleting(true);
    try {
      const res = await fetch(`${API}/api/result-datasets/${activeId}`, { method: 'DELETE' });
      if (!res.ok) {
        const body = await res.json().catch(() => ({}));
        throw new Error(body.error || `HTTP ${res.status}`);
      }
      try { window.localStorage.removeItem(`nrg.dataset.${activeId}`); } catch { /* nothing to clear */ }
      setActiveId(null);
      setDataset(null);
      setSeries([]);
      await loadList();
    } catch (err) {
      setError(`Delete failed: ${err.message || String(err)}`);
    } finally {
      setDeleting(false);
    }
  }, [activeId, loadList]);

  const onPatchSeries = useCallback(async (sid, patch) => {
    if (!activeId) return;
    // Optimistic: the select and the name field must not lag a round trip.
    setSeries((rows) => rows.map((r) => (r.id === sid ? { ...r, ...patch } : r)));
    try {
      const res = await fetch(`${API}/api/result-datasets/${activeId}/series/${sid}`, {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(patch),
      });
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      const updated = await res.json();
      setSeries((rows) => rows.map((r) => (r.id === sid ? { ...r, ...updated } : r)));
      loadDetail(activeId);   // nature counts on the header chips
    } catch (err) {
      setError(`Series update failed: ${err.message || String(err)}`);
    }
  }, [activeId, loadDetail]);

  // ---- render --------------------------------------------------------------
  const ready = dataset && dataset.status === 'ready' && series.length > 0;
  const dot = statusColor(dataset && dataset.status);

  return (
    <div className="ds-view">
      <div className="def-list-header">
        <div>
          <h1>
            <span className="location-code-badge" style={{ fontSize: 14 }}>{project.project_code}</span>
            {' · Dataset'}
          </h1>
          <p className="def-list-subtitle">
            {project.name}
            {dataset && (
              <>
                {' · '}
                <span style={{ color: dot }}>●</span>{' '}
                {dataset.name || 'unnamed'}
                {dataset.series_count ? ` · ${dataset.series_count} series` : ''}
                {spanLabel(dataset) ? ` · ${spanLabel(dataset)}` : ''}
                {` · ${dataset.status}`}
              </>
            )}
          </p>
        </div>
        <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
          {datasets.length > 1 && (
            <select
              className="ds-input ds-input-sm"
              value={activeId || ''}
              onChange={(e) => setActiveId(Number(e.target.value))}
            >
              {datasets.map((d) => (
                <option key={d.id} value={d.id}>{d.name || `dataset ${d.id}`}</option>
              ))}
            </select>
          )}
          <button className="btn-ghost" onClick={onBack}>{'← Back to project'}</button>
        </div>
      </div>

      <div className="climate-toolbar ds-tabbar">
        <div className="climate-toolbar-tabs">
          {TABS.map((t) => (
            <button
              key={t.key}
              className={`toolbar-tab ${tab === t.key ? 'active' : ''}`}
              onClick={() => setTab(t.key)}
              disabled={t.key !== 'overview' && !ready}
              title={t.key !== 'overview' && !ready ? 'available once the dataset is ready' : undefined}
            >
              {t.label}
            </button>
          ))}
        </div>
      </div>

      {error && <div className="ds-error">{error}</div>}

      {loading && !dataset && <CenecLoadingBlock label="Loading datasets…" />}

      {tab === 'overview' && (
        <DatasetOverviewTab
          project={project}
          datasets={datasets}
          dataset={dataset}
          series={series}
          onStarted={onStarted}
          onResume={onResume}
          onDelete={onDelete}
          onPatchSeries={onPatchSeries}
          deleting={deleting}
        />
      )}

      {tab === 'summary' && ready && (
        <DatasetSummaryTab project={project} dataset={dataset} series={series} />
      )}

      {tab === 'explorer' && ready && (
        <DatasetExplorerTab dataset={dataset} series={series} />
      )}
    </div>
  );
};

export default DatasetView;
